import { Button } from "@itandi/itandi-bb-ui/Button";
import { FloatingMenu } from "@itandi/itandi-bb-ui/FloatingMenu";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import useSWRMutation from "swr/mutation";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import Loading from "~/components/common/parts/loading/Loading";
import { ReportImagesArea } from "~/components/common/reports/ReportImagesArea";
import { ReportImagesAreaSp } from "~/components/common/reports/ReportImagesAreaSp";
import { ReportImageUploadPanel } from "~/components/common/reports/ReportImageUploadPanel";
import { useDealInspectionReportImages } from "~/hooks/vendors/deals/useDealInspectionReportImages";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { NskResponse } from "~/models/Api";
import type {
	DealInspectionReportImage,
	DealReportBulkUpdateParams,
	DealReportImage
} from "~/models/DealReportImage";
import {
	NUMBER_OF_DELETE_AT_ONCE,
	NUMBER_OF_UPLOAD_AT_ONCE
} from "~/models/DealReportImage";
import { isSubVendor } from "~/models/DealVendorCompany";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { PATH } from "~/pages/vendors/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { getDownloadPath as getDownloadPdfPath } from "~/utils/api/vendors/deals/DealInspectionReport";
import {
	create as inspectionComplete,
	paths as inspectionPaths
} from "~/utils/api/vendors/deals/DealInspectionReportComplete";
import {
	bulkDelete,
	bulkUpdate,
	destroy,
	getDownloadPath as getDownloadImagePath,
	getDownloadZipPath,
	create as postInspectionReportImages,
	update,
	updateSeq
} from "~/utils/api/vendors/deals/DealInspectionReportImage";
import Styles from "./Page.module.scss";

export function Page() {
	const { notify } = useNotification();
	const router = useRouter();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});

	const [uploading, setUploading] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [processedCount, setProcessedCount] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [selectedImageIds, setSelectedImageIds] = useState<readonly number[]>(
		[]
	);
	const { data: session } = useSessions().index();
	const { data: deal } = useDeals().show(id);
	// globalMutateしたい
	const { data: reportImages, mutate: mutateReportImages } =
		useDealInspectionReportImages().index(id);

	const { trigger, isMutating } = useSWRMutation(
		inspectionPaths.create(id),
		async (_key, { arg }: { arg: { dealId: number } }) =>
			inspectionComplete(arg.dealId)
	);

	const onComplete = useCallback(async () => {
		const result = await trigger({ dealId: id });
		if (isSuccess(result)) {
			await router.push(PATH(id));
			notify({ content: "完了しました。", variant: "success" });
		} else {
			notify({
				content: `エラーが発生しました。${result.message ?? ""}`,
				variant: "error"
			});
		}
	}, [trigger, id, router, notify]);

	if (deal === null || reportImages === null || session === null) {
		return <Loading isLoading />;
	}
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "立会報告画像の登録" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];
	const handleDrop = async (files: File[]): Promise<void> => {
		const total = files.length;
		setProcessedCount(0);
		setTotalCount(total);
		setUploading(true);
		const arrFiles: File[][] = [];
		let finishedFiles = 0;
		while (files.length > 0) {
			arrFiles.push(files.splice(0, NUMBER_OF_UPLOAD_AT_ONCE));
		}
		for (const sendFiles of arrFiles) {
			const result = await postInspectionReportImages(sendFiles, deal.id);
			if (isSuccess(result)) {
				await mutateReportImages();
			} else {
				notify({ content: "エラーが発生しました。", variant: "error" });
				return;
			}
			finishedFiles += sendFiles.length;
			setProcessedCount(finishedFiles);
			await mutateReportImages();
		}
		setUploading(false);
		notify({ content: "画像を登録しました。", variant: "success" });
	};
	const handleUpdate = async (
		submitValue: DealReportImage
	): Promise<NskResponse> => update(submitValue);
	const handleBulkUpdate = async (
		submitValue: DealReportBulkUpdateParams
	): Promise<void> => {
		const result = await bulkUpdate(deal.id, selectedImageIds, submitValue);
		if (isSuccess(result)) {
			await mutateReportImages();
			notify({ content: "更新しました。", variant: "success" });
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	};
	const handleDestroy = async (
		reportImage: DealReportImage
	): Promise<NskResponse> => {
		const result = await destroy(reportImage);
		if (isSuccess(result)) {
			setSelectedImageIds((prev) => {
				const imageIndex = prev.indexOf(reportImage.id);

				return imageIndex === -1 ? prev : prev.toSpliced(imageIndex, 1);
			});
		}
		return result;
	};
	const handleBulkDelete = async (): Promise<void> => {
		if (selectedImageIds.length === 0) {
			return;
		}
		const total = selectedImageIds.length;
		setProcessedCount(0);
		setTotalCount(total);
		setDeleting(true);
		const imageIdArray: number[][] = [];
		let finishedFiles = 0;
		for (
			let i = 0, j = selectedImageIds.length;
			i < j;
			i += NUMBER_OF_DELETE_AT_ONCE
		) {
			imageIdArray.push(
				selectedImageIds.slice(i, i + NUMBER_OF_DELETE_AT_ONCE)
			);
		}
		for (const ids of imageIdArray) {
			const result = await bulkDelete(deal.id, ids);
			if (isSuccess(result)) {
				await mutateReportImages();
			} else {
				notify({ content: "エラーが発生しました。", variant: "error" });
				return;
			}
			finishedFiles += ids.length;
			setProcessedCount(finishedFiles);
			await mutateReportImages();
		}
		setDeleting(false);
		setSelectedImageIds([]);
		notify({ content: "画像を削除しました。", variant: "success" });
	};
	const handleUpdateSeq = async (
		newOrderItems: DealReportImage[]
	): Promise<void> => {
		await mutateReportImages(newOrderItems as DealInspectionReportImage[], {
			revalidate: false
		});
		await updateSeq(newOrderItems, id);
	};
	const handleDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, undefined);
		window.open(path);
	};
	const handleDownloadImage = (reportImage: DealReportImage): void => {
		const path = getDownloadImagePath(reportImage);
		window.open(path);
	};
	const handleDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id);
		window.open(path);
	};
	const handleSelectedDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id, selectedImageIds);
		window.open(path);
	};
	const handleSelectedDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, selectedImageIds);
		window.open(path);
	};
	const onChangeSelectedImage = (selectedImageId: number): void => {
		if (selectedImageIds.includes(selectedImageId)) {
			setSelectedImageIds(
				selectedImageIds.filter((imageId) => imageId !== selectedImageId)
			);
		} else {
			setSelectedImageIds(selectedImageIds.concat([selectedImageId]));
		}
	};
	const handleResetSelectedIds = (): void => {
		setSelectedImageIds([]);
	};
	const handleSelectAllImages = (): void => {
		setSelectedImageIds(reportImages.map((image: DealReportImage) => image.id));
	};
	return (
		<VendorLayoutV2>
			<VendorDealLayout deal={deal} enableChat items={items} noBackground>
				<div className={Styles.MainWrapper}>
					<h2 className={Styles.ReportTitle}>立会報告画像の登録</h2>
					<ReportImageUploadPanel
						deleting={deleting}
						handleDrop={handleDrop}
						processedCount={processedCount}
						totalCount={totalCount}
						uploading={uploading}
					/>
					<ReportImagesArea
						companyType="vendor"
						handleBulkDelete={handleBulkDelete}
						handleBulkUpdate={handleBulkUpdate}
						handleDestroy={handleDestroy}
						handleDownloadImage={handleDownloadImage}
						handleDownloadPdf={handleDownloadPdf}
						handleDownloadZip={handleDownloadZip}
						handleResetSelectedIds={handleResetSelectedIds}
						handleSelectAllImages={handleSelectAllImages}
						handleSelectedDownloadPdf={handleSelectedDownloadPdf}
						handleSelectedDownloadZip={handleSelectedDownloadZip}
						handleUpdate={handleUpdate}
						handleUpdateSeq={handleUpdateSeq}
						onChangeSelectedImage={onChangeSelectedImage}
						reportImages={reportImages}
						selectedImageIds={selectedImageIds}
					/>
					<ReportImagesAreaSp
						companyType="vendor"
						handleBulkDelete={handleBulkDelete}
						handleBulkUpdate={handleBulkUpdate}
						handleDestroy={handleDestroy}
						handleDownloadImage={handleDownloadImage}
						handleDownloadPdf={handleDownloadPdf}
						handleDownloadZip={handleDownloadZip}
						handleResetSelectedIds={handleResetSelectedIds}
						handleSelectAllImages={handleSelectAllImages}
						handleSelectedDownloadPdf={handleSelectedDownloadPdf}
						handleSelectedDownloadZip={handleSelectedDownloadZip}
						handleUpdate={handleUpdate}
						handleUpdateSeq={handleUpdateSeq}
						onChangeSelectedImage={onChangeSelectedImage}
						reportImages={reportImages}
						selectedImageIds={selectedImageIds}
					/>
				</div>
				{selectedImageIds.length === 0 && (
					<FloatingMenu
						fitToParent
						rightArea={
							<Button
								loading={isMutating}
								onClick={onComplete}
								variant="primary"
							>
								{isSubVendor(deal, session.vendorCompany.id)
									? "完了して通知"
									: "完了して戻る"}
							</Button>
						}
					/>
				)}
			</VendorDealLayout>
		</VendorLayoutV2>
	);
}
